/**
 * Ring / donut geometry. Angles are in radians, measured clockwise from 12 o'clock (so 0 is the top and π/2 is the
 * right), which is how a progress ring and a donut are read.
 */
import { clamp, fraction, isFiniteNumber, roundCoord } from './numbers.js';
export const TAU = Math.PI * 2;
/** Spans closer to a full turn than this are drawn as a full circle (an SVG arc cannot start and end on one point). */
const FULL_TURN_EPSILON = 1e-6;
/** The point at `angle` on the circle of radius `r` around (cx, cy). */
export function polarPoint(cx, cy, r, angle) {
    return { x: cx + r * Math.sin(angle), y: cy - r * Math.cos(angle) };
}
/**
 * Lay values out round a ring. Negative and non-finite values count as 0. With no positive value (or a non-positive
 * total) every segment is empty — the caller shows its empty state.
 */
export function ringSegments(values, options = {}) {
    const start = isFiniteNumber(options.startAngle) ? options.startAngle : 0;
    const clean = values.map((v) => (isFiniteNumber(v) && v > 0 ? v : 0));
    const sum = clean.reduce((a, b) => a + b, 0);
    const whole = isFiniteNumber(options.total) && options.total > 0 ? Math.max(options.total, sum) : sum;
    const drawn = clean.filter((v) => v > 0).length;
    const gap = drawn > 1 && isFiniteNumber(options.gap) ? Math.max(0, options.gap) : 0;
    let cursor = 0;
    return clean.map((value, index) => {
        const f = fraction(value, whole);
        const startFraction = cursor;
        cursor += f;
        const span = f * TAU;
        // Trim half a gap from each side, but never past the slice's own middle.
        const trim = Math.min(gap / 2, span / 2);
        const a0 = start + startFraction * TAU + (f > 0 ? trim : 0);
        const a1 = start + cursor * TAU - (f > 0 ? trim : 0);
        return { index, value, fraction: f, startFraction, endFraction: cursor, startAngle: a0, endAngle: a1 };
    });
}
function n(value) {
    return String(roundCoord(value));
}
function fullRing(cx, cy, outer, inner) {
    const circle = (r) => `M${n(cx)} ${n(cy - r)}A${n(r)} ${n(r)} 0 1 1 ${n(cx)} ${n(cy + r)}A${n(r)} ${n(r)} 0 1 1 ${n(cx)} ${n(cy - r)}Z`;
    // Two subpaths: fill with `fill-rule="evenodd"` to cut the hole.
    return inner > 0 ? `${circle(outer)}${circle(inner)}` : circle(outer);
}
/**
 * The closed path of an annular sector (a donut slice) or, with `innerRadius` 0, a pie wedge. An empty or inverted span
 * returns `''`. A full turn returns the whole ring as two subpaths — fill it with `fill-rule="evenodd"`.
 */
export function arcPath(input) {
    const outer = Math.max(0, input.outerRadius);
    const inner = clamp(input.innerRadius ?? 0, 0, outer);
    const span = input.endAngle - input.startAngle;
    if (!(span > 0) || outer === 0 || !isFiniteNumber(span))
        return '';
    if (span >= TAU - FULL_TURN_EPSILON)
        return fullRing(input.cx, input.cy, outer, inner);
    const large = span > Math.PI ? 1 : 0;
    const o0 = polarPoint(input.cx, input.cy, outer, input.startAngle);
    const o1 = polarPoint(input.cx, input.cy, outer, input.endAngle);
    if (inner === 0)
        return `M${n(input.cx)} ${n(input.cy)}L${n(o0.x)} ${n(o0.y)}A${n(outer)} ${n(outer)} 0 ${large} 1 ${n(o1.x)} ${n(o1.y)}Z`;
    const i1 = polarPoint(input.cx, input.cy, inner, input.endAngle);
    const i0 = polarPoint(input.cx, input.cy, inner, input.startAngle);
    return `M${n(o0.x)} ${n(o0.y)}A${n(outer)} ${n(outer)} 0 ${large} 1 ${n(o1.x)} ${n(o1.y)}L${n(i1.x)} ${n(i1.y)}A${n(inner)} ${n(inner)} 0 ${large} 0 ${n(i0.x)} ${n(i0.y)}Z`;
}
/** The open centre-line path of an arc, to draw with `stroke` and a round cap (a progress ring). A full turn is two half arcs. */
export function strokeArcPath(input) {
    const r = Math.max(0, input.radius);
    const span = input.endAngle - input.startAngle;
    if (!(span > 0) || r === 0 || !isFiniteNumber(span))
        return '';
    const p0 = polarPoint(input.cx, input.cy, r, input.startAngle);
    if (span >= TAU - FULL_TURN_EPSILON) {
        const p1 = polarPoint(input.cx, input.cy, r, input.startAngle + Math.PI);
        return `M${n(p0.x)} ${n(p0.y)}A${n(r)} ${n(r)} 0 1 1 ${n(p1.x)} ${n(p1.y)}A${n(r)} ${n(r)} 0 1 1 ${n(p0.x)} ${n(p0.y)}`;
    }
    const p1 = polarPoint(input.cx, input.cy, r, input.endAngle);
    return `M${n(p0.x)} ${n(p0.y)}A${n(r)} ${n(r)} 0 ${span > Math.PI ? 1 : 0} 1 ${n(p1.x)} ${n(p1.y)}`;
}
/** The circumference of a circle of radius `r`. A ring drawn as a stroked circle uses it for `stroke-dasharray`. */
export function circumference(r) {
    return TAU * Math.max(0, r);
}
//# sourceMappingURL=arc.js.map